import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import Link from 'next/link';

import { theme } from '_constants/theme';
import { FIELDS_KEYS } from '_constants/forms';
import { ROUTES } from '_constants/routes';

import { getFieldProps } from '_helpers/forms/fields';

import * as Layout from '_components/atomic/layouts';
import BaseField from './fields/BaseField';
import CheckboxField from './fields/CheckboxField';
import Button from './Button';

const StyledForm = styled(Form)``;

const StyledContainer = styled.div``;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: center;

  button {
    justify-content: center;
    width: 100%;
    padding-top: ${theme.spaces.small2X}px;
    padding-bottom: ${theme.spaces.small2X}px;
    padding-left: ${theme.spaces.regular}px;
    padding-right: ${theme.spaces.regular}px;
  }
`;

const StyledSpan = styled.span`
  font-size: ${theme.fontSizes.smallX}px;
  line-height: 130%;

  a {
    font-weight: 700;
    color: ${theme.colors.indigo[600]};
    text-decoration: unset;

    &:hover {
      text-decoration: underline;
    }
  }

  @media ${theme.devices.tablet} {
    font-size: ${theme.fontSizes.small}px;
  }
`;

const SubscribeForTipsForm = ({ error, success, submitDisabled, onSubmit }) => {
  const {
    control,
    formState: { isValidating },
    reset,
    handleSubmit,
  } = useForm({
    defaultValues: { [FIELDS_KEYS.fullName]: '', [FIELDS_KEYS.email]: '' },
    reValidateMode: 'onChange',
  });

  const [emailVerifying, setEmailVerifying] = useState(false);

  useEffect(() => {
    if (success) {
      reset();
    }
  }, [success, reset]);

  return (
    <StyledForm noValidate onSubmit={handleSubmit(onSubmit)}>
      <StyledContainer>
        <Layout.Card.Text size={theme.fontSizes.small} boldest centered desktop>
          Get Fresh Monthly Tips to
          <br />
          Start & Grow Your LLC
        </Layout.Card.Text>
        <Layout.Card.Text size={theme.fontSizes.large} boldest centered mobile>
          Get Fresh Monthly Tips to
          <br />
          Start & Grow Your LLC
        </Layout.Card.Text>
        <BaseField {...getFieldProps(FIELDS_KEYS.fullName)} control={control} />
        <BaseField
          {...getFieldProps(FIELDS_KEYS.email, {
            validationOptions: { setProcessing: setEmailVerifying },
          })}
          control={control}
          loading={emailVerifying}
          feedback={emailVerifying ? 'Verifying...' : undefined}
        />
        <CheckboxField
          {...getFieldProps(FIELDS_KEYS.agreeWithTerms)}
          label={
            <StyledSpan>
              I agree to&nbsp;<Link href={ROUTES.terms}>Terms of Service</Link>
              <br />
              and&nbsp;<Link href={ROUTES.privacy}>Privacy Policy</Link>
            </StyledSpan>
          }
          control={control}
        />
        <StyledButtonContainer>
          <Button
            type="submit"
            small
            disabled={emailVerifying || isValidating || submitDisabled}
            color={theme.colors.indigo[500]}
            borderColor={theme.colors.indigo[600]}
          >
            SUBSCRIBE
          </Button>
        </StyledButtonContainer>
        {success && (
          <>
            <Layout.Divider size={theme.spaces.regular} />
            <Layout.Card.Text
              color={theme.colors.green[500]}
              bold
              centered
              noBottomSpace
            >
              Thank you for subscription.
            </Layout.Card.Text>
          </>
        )}
        {error && (
          <>
            <Layout.Divider size={theme.spaces.regular} />
            <Layout.Card.Text
              color={theme.colors.orange[500]}
              bold
              centered
              noBottomSpace
            >
              {error?.message}
            </Layout.Card.Text>
          </>
        )}
      </StyledContainer>
    </StyledForm>
  );
};

SubscribeForTipsForm.propTypes = {
  error: PropTypes.object,
  success: PropTypes.bool,
  submitDisabled: PropTypes.bool,
  onSubmit: PropTypes.func,
};

export default SubscribeForTipsForm;
